import React, { useCallback, useContext, useEffect, useState, memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RadioConfigProps, CmOption, RadioConfigValue } from './types';
import { createOption } from './utils';
import { OPTION_DEBOUNCE_MS } from './cm-control-config.constants';
import ConfirmPopup from '../../ModalPopup/confirm-popup';
import LibraryTitleInput from '../library-title-input';
import CustomTooltip from '../../Tooltip/CustomTooltip';
import { NewCheckbox } from '../../PIM/common/NewCheckbox';
import { NewRadio } from '../../PIM/common/NewRadio';
import { NewInput } from '../../PIM/common/NewInput';
import OptionRow, { moveOption, useOptionReorder } from './option-row';
import FieldSetupPanelContext from './field-setup-panel.context';

function useDebouncedEmit<T>(onEmit: (value: T) => void, delayMs: number) {
  const valueRef = useRef<T>(null as unknown as T);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleEmit = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      onEmit(valueRef.current);
    }, delayMs);
  }, [onEmit, delayMs]);

  const onEmitRef = useRef(onEmit);
  onEmitRef.current = onEmit;

  const flushPending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      onEmitRef.current(valueRef.current);
    }
  }, []);

  useEffect(() => flushPending, [flushPending]);

  return { valueRef, scheduleEmit };
}

/**
 * RadioConfig component for configuring radio button form controls in the system canvas.
 * Allows users to set up radio button options, required field settings, and save configurations to library.
 * @param value - Current radio configuration value
 * @param onChange - Callback function called when configuration changes
 * @param className - Optional CSS class name for styling
 * @param showLibraryTitle - Whether to show the library title input section
 */
const RadioConfig: React.FC<RadioConfigProps> = ({ value, onChange, className = '', showLibraryTitle = true, width, onWidthChange, widthError, hideTitle = false }) => {
  const { t } = useTranslation('custom-module-system-canvas');
  const titlePlaceholder = t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER');
  const setupPanel = useContext(FieldSetupPanelContext);
  const isInSetupCard = setupPanel !== null;
  const showOptionsPart = setupPanel !== 'setup';
  const showWidthRow = onWidthChange != null;

  const [localValue, setLocalValue] = useState<RadioConfigValue>(value);
  const [libraryTitle, setLibraryTitle] = useState<string>('');
  const optionInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const justEmittedRef = useRef(false);

  const onEmit = useCallback(
    (v: RadioConfigValue) => {
      justEmittedRef.current = true;
      onChange(v);
    },
    [onChange],
  );
  const { valueRef: latestValueRef, scheduleEmit: debouncedEmit } = useDebouncedEmit<RadioConfigValue>(onEmit, OPTION_DEBOUNCE_MS);

  useEffect(() => {
    if (justEmittedRef.current) {
      justEmittedRef.current = false;
      latestValueRef.current = value;
      return;
    }
    setLocalValue(value);
    latestValueRef.current = value;
  }, [value, latestValueRef]);

  useEffect(() => {
    const lastIndex = localValue.options.length - 1;
    if (lastIndex < 0) return;
    const timer = setTimeout(() => {
      optionInputRefs.current[lastIndex]?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [localValue.options.length]);

  const updateTitle = useCallback(
    (next: string) => {
      const prev = latestValueRef.current;
      const nextVal = { ...prev, title: next };
      latestValueRef.current = nextVal;
      setLocalValue(nextVal);
      onChange(nextVal);
    },
    [onChange],
  );

  const toggleRequired = useCallback(() => {
    const prev = latestValueRef.current;
    const nextVal = { ...prev, required: !prev.required };
    latestValueRef.current = nextVal;
    setLocalValue(nextVal);
    onChange(nextVal);
  }, [onChange]);

  const updateOptionAt = useCallback(
    (index: number, nextLabel: string) => {
      setLocalValue((prev) => {
        const prevLabel = prev.options[index]?.label;
        const nextOptions = prev.options.map((option, i) => (i === index ? { ...option, label: nextLabel } : option));
        const nextValue = prev.value === prevLabel ? nextLabel : prev.value;
        const nextVal = { ...prev, options: nextOptions, ...(nextValue !== undefined ? { value: nextValue } : {}) };
        latestValueRef.current = nextVal;
        return nextVal;
      });
      debouncedEmit();
    },
    [debouncedEmit],
  );

  const removeOptionAt = useCallback(
    (index: number) => {
      const prev = latestValueRef.current;
      const removedLabel = prev.options[index]?.label;
      const nextOptions = prev.options.filter((_, i) => i !== index);
      optionInputRefs.current = optionInputRefs.current.filter((_, i) => i !== index);
      const nextValue = prev.value === removedLabel ? null : prev.value;
      const nextVal = {
        ...prev,
        options: nextOptions.length ? nextOptions : [createOption('')],
        ...(nextValue !== undefined ? { value: nextValue } : {}),
      };
      latestValueRef.current = nextVal;
      setLocalValue(nextVal);
      onChange(nextVal);
    },
    [onChange],
  );

  const addNewOption = useCallback(() => {
    justEmittedRef.current = true;
    const prev = latestValueRef.current;
    const nextVal = { ...prev, options: [...prev.options, createOption('')] };
    latestValueRef.current = nextVal;
    setLocalValue(nextVal);
    onChange(nextVal);
  }, [onChange]);

  const setPreset = useCallback(
    (optionLabel: string) => {
      const prev = latestValueRef.current;
      const next = { ...prev, value: optionLabel };
      latestValueRef.current = next;
      setLocalValue(next);
      onChange(next);
    },
    [onChange],
  );

  const clearPreset = useCallback(() => {
    const prev = latestValueRef.current;
    const next = { ...prev, value: null };
    latestValueRef.current = next;
    setLocalValue(next);
    onChange(next);
  }, [onChange]);

  const [confirmIndex, setConfirmIndex] = useState<number | null>(null);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);

  /**
   * Initiates the option removal confirmation process for a radio button option
   * @param index - Index of the option to be removed
   */
  const requestRemoveOption = useCallback(
    (index: number) => {
      if (showConfirm) return;
      setConfirmIndex(index);
      setShowConfirm(true);
    },
    [showConfirm],
  );

  /**
   * Confirms and executes the removal of a radio option
   */
  const handleConfirmDelete = useCallback(() => {
    if (confirmIndex !== null) {
      removeOptionAt(confirmIndex);
    }
    setShowConfirm(false);
    setConfirmIndex(null);
  }, [confirmIndex, removeOptionAt]);

  /**
   * Cancels the option removal confirmation
   */
  const handleCancelDelete = useCallback(() => {
    setShowConfirm(false);
    setConfirmIndex(null);
  }, []);

  const handleLibrarySave = useCallback(() => {
    setLibraryTitle('');
  }, []);

  const handleLibrarySelect = useCallback(
    (controlTypeData: any) => {
      const fetchedConfig = controlTypeData?.content?.content as RadioConfigValue | undefined;
      if (fetchedConfig) {
        setLocalValue(fetchedConfig);
        onChange(fetchedConfig);
      }
    },
    [onChange],
  );

  const moveOptionAt = useCallback(
    (fromIndex: number, toIndex: number) => {
      const prev = latestValueRef.current;
      const nextVal = { ...prev, options: moveOption(prev.options, fromIndex, toIndex) };
      latestValueRef.current = nextVal;
      setLocalValue(nextVal);
      onChange(nextVal);
    },
    [onChange],
  );

  const optionReorder = useOptionReorder(moveOptionAt);

  return (
    <div className={`cm-maxw-520 ${className}`}>
      {showWidthRow && (
        <div className="mb-2">
          <label className="f-12 d-block">Width (%)</label>
          <NewInput
            type="text"
            placeholder="e.g. 25% or 150px"
            value={width ?? ''}
            onChange={(e) => onWidthChange(e.target.value.trim())}
            title="Field width when rendered (e.g. 25% or 150px)"
            invalid={!!widthError}
          />
          {widthError && <div className="invalid-feedback d-block">{widthError}</div>}
        </div>
      )}
      {showWidthRow && (
        <div className="mb-2">
          <p className="m-0">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.RADIO.TITLE')}</p>
          <NewInput type="text" placeholder={titlePlaceholder} value={localValue.title} onChange={(e) => updateTitle(e.target.value)} />
        </div>
      )}
      {showLibraryTitle && (
        <LibraryTitleInput
          value={libraryTitle}
          onChange={setLibraryTitle}
          onSave={handleLibrarySave}
          onSelect={handleLibrarySelect}
          controlType="radio"
          contentData={localValue}
          placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER_LIBRARY_TITLE')}
        />
      )}
      {!isInSetupCard && (
      <div className="mb-1 mt-2 d-flex align-items-center cm-checkbox">
        <label className="m-0">
          {t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')}
          <span className="cm-field-required">*</span>
        </label>
        {!localValue.title?.trim() ? (
          <CustomTooltip tooltipText={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_CHECKBOX_TOOLTIP')}>
            <NewCheckbox className="mb-1 mr-0" checked={!!localValue.required} onChange={toggleRequired} disabled={!localValue.title?.trim()} aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')} />
          </CustomTooltip>
        ) : (
          <NewCheckbox className="mb-1 mr-0" checked={!!localValue.required} onChange={toggleRequired} aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')} />
        )}
      </div>
      )}

      {!showWidthRow && !hideTitle && (
        <div className="mb-2">
          <p className="m-0">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.RADIO.TITLE')}</p>
          <NewInput type="text" placeholder={titlePlaceholder} value={localValue.title} onChange={(e) => updateTitle(e.target.value)} />
        </div>
      )}

      {showOptionsPart && (
      <div className={isInSetupCard ? '' : 'pt-2'}>
        {!isInSetupCard && (
        <div className="d-flex align-items-center justify-content-between">
          <p className="m-0 font-weight-500">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.RADIO.LABEL')}</p>
        </div>
        )}

        {!isInSetupCard && (
        <div className="mt-2 mb-1 d-flex align-items-center justify-content-between">
          <p className="m-0">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.RADIO.OPTIONS')}</p>
          {(localValue.value ?? null) != null && (
            <button
              type="button"
              className="btn btn-link p-0 border-0 text-decoration-none small"
              onClick={clearPreset}
              aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.RADIO.CLEAR_DEFAULT')}
            >
              {t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.RADIO.CLEAR_DEFAULT')}
            </button>
          )}
        </div>
        )}

        <div>
          {localValue.options.map((option: CmOption, index: number) => (
            <OptionRow
              key={option.id || index}
              index={index}
              label={option.label}
              placeholder={titlePlaceholder}
              inputRef={(el) => {
                optionInputRefs.current[index] = el;
              }}
              onLabelChange={(nextLabel) => updateOptionAt(index, nextLabel)}
              isLast={index === localValue.options.length - 1}
              canDelete={localValue.options.length > 1}
              onAdd={addNewOption}
              onDelete={() => requestRemoveOption(index)}
              isDragOver={optionReorder.dragOverIndex === index}
              onReorderStart={optionReorder.onReorderStart}
              onReorderOver={optionReorder.onReorderOver}
              onReorderDrop={optionReorder.onReorderDrop}
              onReorderEnd={optionReorder.onReorderEnd}
              defaultControl={
                <NewRadio
                  name="radio-preset"
                  checked={(localValue.value ?? null) === option.label}
                  onClick={(e) => {
                    e.preventDefault();
                    if ((localValue.value ?? null) === option.label) {
                      clearPreset();
                    } else {
                      setPreset(option.label);
                    }
                  }}
                  onChange={() => {}}
                  aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.RADIO.SET_AS_DEFAULT')}
                />
              }
            />
          ))}
        </div>
      </div>
      )}
      <ConfirmPopup
        onOpen={showConfirm}
        title={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.RADIO.DELETE_OPTION_TITLE')}
        message={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.RADIO.DELETE_OPTION_MESSAGE')}
        toggle={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default memo(RadioConfig);
